import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const PWAInstallPrompt = () => {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches 
      || window.navigator?.standalone 
      || (typeof document.referrer === 'string' && document.referrer.includes('android-app://'));
    setIsStandalone(standalone);

    // Check if iOS
    const ua = navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    // Don't show if already installed
    if (standalone) return;

    // Check if user dismissed before (with 7-day cooldown)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysDiff = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysDiff < 7) return;
    }

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after user has interacted with the app a bit
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show prompt after delay (no beforeinstallprompt event)
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't render if installed or prompt hidden
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-[#0B1F3B] text-white rounded-xl shadow-2xl p-4 border border-white/10">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-slate-800 rounded-full transition-colors"
          aria-label={t('pwa.hc_sluiten')}
          data-testid="pwa-dismiss-button"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-600 rounded-lg shrink-0">
            <Smartphone className="w-6 h-6" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base mb-1">
              Installeer SmartCMR
            </h3>
            
            {isIOS ? (
              <div className="text-sm text-slate-300 space-y-2">
                <p>{t('pwa.hc_installeerDezeAppOp')}</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Tik op het <span className="inline-flex items-center px-1 bg-slate-700 rounded">{t('pwa.hc_deel')}</span> icoon</li>
                  <li>{t('pwa.hc_scrollNaarBeneden')}</li>
                  <li>Tik op <strong>{t('pwa.hc_zetOpBeginscherm')}</strong></li>
                </ol>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-300 mb-3">
                  Voeg de app toe aan je startscherm voor snelle toegang en offline gebruik.
                </p>
                <Button 
                  onClick={handleInstall}
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="pwa-install-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Installeren
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
